/**
 * @author Nihar <nihar.baishya@314ecorp.com>
 * @description store for claims requests
 */
// TODO
import { create } from 'zustand';
import _ from 'lodash';
import { devtools, persist, createJSONStorage } from 'zustand/middleware';
import { dateType, EXPORT_OPTIONS } from 'utils/eligibility/eligibilityRequests';
import { mapClaimStatusLabelToCodes } from 'utils/claims/claimStatusRequests';
import { covertDateToStringArray, DATE_PRESETS } from 'utils/dateParser';

const defaultPagination = {
	pageSize: 10,
	pageNumber: 1,
};

const defaultExportMetaData = {
	visible: false,
	dateRange: { key: dateType.radio, range: EXPORT_OPTIONS[0].value },
};

const defaultBatchResubmitMetaData = {
	visible: false,
	dateRange: { key: dateType.radio, range: EXPORT_OPTIONS[0].value },
	newDateRange: covertDateToStringArray(DATE_PRESETS.today.value),
};

const initialState: ClaimStatusRequests.IValues = {
	collapsed: true,
	pageSize: defaultPagination.pageSize,
	pageNumber: defaultPagination.pageNumber,
	advancedSearchParams: {},
	filterParams: {},
	apiParams: {},
	appliedParams: {},
	exportMetaData: defaultExportMetaData,
	batchResubmitMetaData: defaultBatchResubmitMetaData,
};

const useClaimStatusRequestsStore = create<ClaimStatusRequests.IStore>()(
	persist(
		devtools(
			(set) => ({
				...initialState,
				actions: {
					resetStates: () =>
						set(() => ({
							collapsed: true,
							searchTerm: '',
						})),

					setCollapsed: (collapsed) => set(() => ({ collapsed })),
					setAdvancedSearchParams: (advancedSearchParams) => set(() => ({ advancedSearchParams })),
					setFilterParams: (filterParams) => set(() => ({ filterParams })),
					setPageSize: (pageSize) => set(() => ({ pageSize })),
					setPageNumber: (pageNumber) => set(() => ({ pageNumber })),
					setAppliedParams: (appliedParams) => {
						set(() => ({
							appliedParams,
							pageNumber: defaultPagination.pageNumber,
						}));

						set(() => ({
							apiParams: {
								...(appliedParams.advancedSearchParams || {}),
								...(appliedParams.filterParams || {}),
								...(_.isEmpty(appliedParams?.filterParams?.status)
									? {}
									: { status: mapClaimStatusLabelToCodes(appliedParams?.filterParams?.status) }),
							},
						}));
					},

					setExportMetaData: (exportMetaData) => set(() => ({ exportMetaData })),
					setBatchResubmitMetaData: (batchResubmitMetaData) => set(() => ({ batchResubmitMetaData })),
					closeExportModal: () => set(() => ({ exportMetaData: defaultExportMetaData })),
					closeBatchResubmissionModal: () => set(() => ({ batchResubmitMetaData: defaultBatchResubmitMetaData })),
				},
			}),
			{ name: 'Claims Requests' },
		),
		{
			name: 'claims-requests',
			storage: createJSONStorage(() => sessionStorage),
			partialize: (state) => ({
				pageNumber: state.pageNumber,
				pageSize: state.pageSize,
				advancedSearchParams: state.advancedSearchParams,
				filterParams: state.filterParams,
				appliedParams: state.appliedParams,
			}),
			onRehydrateStorage: () => (state) => {
				if (state && state.appliedParams) {
					const combinedParams = {
						...(state.appliedParams.advancedSearchParams || {}),
						...(state.appliedParams.filterParams || {}),
					};

					// Convert claimAge to number if it exists during rehydration
					const claimAge = combinedParams.claimAge ? parseInt(String(combinedParams.claimAge), 10) : undefined;
					// Ensure claimAge is a valid number or undefined
					const validClaimAge = !isNaN(claimAge) ? claimAge : undefined;

					state.apiParams = {
						...combinedParams,
						claimAge: validClaimAge,
						...(_.isEmpty(state.appliedParams?.filterParams?.status)
							? {}
							: { status: mapClaimStatusLabelToCodes(state.appliedParams?.filterParams?.status) }),
					};
				}
			},
		},
	),
);

// store to return actions and values
const useClaimStatusRequestsStoreValues = (): ClaimStatusRequests.IValues =>
	useClaimStatusRequestsStore((state) => _.omit(state, ['actions']));
const useClaimStatusRequestsStoreActions = (): ClaimStatusRequests.IActions => useClaimStatusRequestsStore((state) => state.actions);

export { defaultPagination, useClaimStatusRequestsStoreValues, useClaimStatusRequestsStoreActions };
export default useClaimStatusRequestsStore;
